import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "react-bootstrap";
import logo from "../assets/images/logo2.png";
import loginBg from "../assets/images/login-bg.png";
import axios from "axios";
import ModalAlert from "../components/reusable/ModalAlert";
import { useHistory } from "react-router-dom";
import { useAppContext } from "../config/Context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faLock,
  faEye,
  faEyeSlash,
  faSignInAlt,
  faEnvelope,
  faShieldAlt,
  faArrowLeft,
  faRedo,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import sendOtp from "./TwoFactor/sendOtp";
import verifyOtp from "./TwoFactor/verifyOtp";
const crypto = require("crypto");

function Login() {
  const history = useHistory();
  const context = useAppContext();

  // --- Credentials step ---
  const [mobileNumber, setMobileNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [showForgotPasswordAlert, setShowForgotPasswordAlert] = useState(false);
  const [validationError, setValidationError] = useState({});
  const [isInactive, setIsInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAlertMsg, setLoginAlertMsg] = useState("");

  // --- OTP step ---
  const [step, setStep] = useState("credentials");
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendTick, setResendTick] = useState(0);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [cardVisible, setCardVisible] = useState(true);

  const otpRefs = useRef([]);

  // --- Resend countdown ---
  useEffect(() => {
    if (step !== "otp") return;
    setResendTimer(60);
    setCanResend(false);
    const id = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) { clearInterval(id); setCanResend(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [step, resendTick]);

  // --- Helpers ---

  function isTrustedDevice(mobile) {
    const stored = localStorage.getItem(`mwa_trust_${mobile}`);
    return stored ? Date.now() < parseInt(stored, 10) : false;
  }

  function finalizeLogin({ user, accessToken }) {
    context.setUser(user);
    if (user) localStorage.setItem("user", JSON.stringify(user));
    if (accessToken) localStorage.setItem("accessToken", accessToken);
    history.push("/");
  }

  function transitionTo(nextStep, resetOtp = false) {
    setCardVisible(false);
    setTimeout(() => {
      setStep(nextStep);
      if (resetOtp) {
        setOtpValues(["", "", "", "", "", ""]);
        setOtpError("");
        setTrustDevice(false);
      }
      setCardVisible(true);
    }, 300);
  }

  async function triggerOtpStep(data) {
    const res = await sendOtp(mobileNumber);
    if (res?.data) {
      setPendingLoginData(data);
      transitionTo("otp");
    } else {
      setLoginAlertMsg("Failed to send OTP to your email. Please try again.");
      setShowLoginAlert(true);
    }
  }

  // --- Credentials submit ---
  function loginHandler(event) {
    event.preventDefault();

    if (mobileNumber.length !== 10) {
      setValidationError({ errorMesage: "Please enter a valid 10-digit mobile number." });
      setShowLoginAlert(true);
      return;
    }
    setValidationError({});
    setIsLoading(true);

    const encodedPass = crypto
      .pbkdf2Sync(password, process.env.REACT_APP_PASSWORD_SECRET, 1000, 64, "sha512")
      .toString("hex");

    axios
      .post(`${process.env.REACT_APP_API_URL}/api/user/login`, {
        mobileNumber,
        password: encodedPass,
      })
      .then(async (res) => {
        if (res.data.message === "Login success") {
          const data = { user: res.data.user, accessToken: res.data.accessToken };
          if (isTrustedDevice(mobileNumber)) {
            setIsLoading(false);
            finalizeLogin(data);
          } else {
            // Keep button in loading state while OTP email is being sent
            await triggerOtpStep(data);
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
          setLoginAlertMsg("");
          setShowLoginAlert(true);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response?.data?.Message === "User Inactive") {
          setIsInactive(true);
          setShowLoginAlert(true);
        } else {
          setLoginAlertMsg("");
          setShowLoginAlert(true);
        }
      });
  }

  // --- OTP handlers ---

  async function otpSubmitHandler(directOtp) {
    const otp = directOtp !== undefined ? directOtp : otpValues.join("");
    if (otp.length < 6) {
      setOtpError("Please enter all 6 digits.");
      return;
    }
    setOtpError("");
    setIsVerifyingOtp(true);
    const res = await verifyOtp(mobileNumber, otp);
    setIsVerifyingOtp(false);

    if (res?.data?.message === "OTP verified") {
      if (trustDevice) {
        const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000;
        localStorage.setItem(`mwa_trust_${mobileNumber}`, String(expiry));
      }
      finalizeLogin(res.data);
    } else {
      setOtpError("Invalid or expired OTP. Please try again.");
      setOtpValues(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    }
  }

  function handleOtpChange(index, value) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otpValues];
    next[index] = digit;
    setOtpValues(next);

    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    } else if (digit && index === 5) {
      // Auto-submit when last digit is entered — pass filled array directly to avoid stale state
      otpSubmitHandler(next.join(""));
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) otpRefs.current[index + 1]?.focus();
  }

  function handleOtpPaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setOtpValues(next);
    const focusIndex = Math.min(pasted.length, 5);
    otpRefs.current[focusIndex]?.focus();
    if (pasted.length === 6) otpSubmitHandler(next.join(""));
  }

  async function handleResendOtp() {
    if (!canResend || isResending) return;
    setIsResending(true);
    setOtpValues(["", "", "", "", "", ""]);
    setOtpError("");
    const res = await sendOtp(mobileNumber);
    setIsResending(false);
    if (res?.data) {
      setResendTick((t) => t + 1);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } else {
      setOtpError("Failed to resend OTP. Please try again.");
    }
  }

  function handleBackToCredentials() {
    transitionTo("credentials", true);
    setPendingLoginData(null);
  }

  const resetCallback = () => {
    setShowLoginAlert(false);
    setIsInactive(false);
  };

  return (
    <div style={{ ...styles.page, backgroundImage: `url(${loginBg})` }}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrap}>
          <img src={logo} alt="Manshya" style={styles.logo} />
        </div>

        {/* Animated step wrapper */}
        <div
          style={{
            ...styles.stepWrapper,
            opacity: cardVisible ? 1 : 0,
            transform: cardVisible ? "translateY(0)" : "translateY(12px)",
          }}
        >
          {/* ── Step 1: Credentials ── */}
          {step === "credentials" && (
            <form onSubmit={loginHandler}>
              <h2 style={styles.heading}>Welcome back</h2>
              <p style={styles.subheading}>Sign in to your account to continue</p>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>MOBILE NUMBER</label>
                <div style={styles.inputWrap}>
                  <FontAwesomeIcon icon={faMobileAlt} style={styles.inputIcon} />
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    placeholder="9011001895"
                    maxLength={10}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>PASSWORD</label>
                <div style={styles.inputWrap}>
                  <FontAwesomeIcon icon={faLock} style={styles.inputIcon} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{ ...styles.input, paddingRight: "2.5rem" }}
                    onKeyDown={(e) => { if (e.key === "Enter") loginHandler(e); }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeBtn}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} style={{ color: "#aab8b0" }} />
                  </button>
                </div>
              </div>

              <div style={{ textAlign: "right", marginBottom: "1.5rem" }}>
                <button type="button" onClick={() => setShowForgotPasswordAlert(true)} style={styles.forgotBtn}>
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                style={isLoading ? { ...styles.loginBtn, opacity: 0.8, cursor: "not-allowed" } : styles.loginBtn}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" style={styles.btnSpinner} />
                    Signing in...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSignInAlt} style={{ marginRight: "0.5rem" }} />
                    Login
                  </>
                )}
              </button>
            </form>
          )}

          {/* ── Step 2: OTP Verification ── */}
          {step === "otp" && (
            <div>
              <button type="button" onClick={handleBackToCredentials} style={styles.backBtn}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: "0.4rem" }} />
                Back
              </button>

              <h2 style={styles.heading}>Verify your identity</h2>
              <p style={styles.subheading}>
                <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "0.45rem", color: "#29553D" }} />
                Enter the 6-digit code sent to your registered email
              </p>

              {/* 6 OTP boxes */}
              <div style={styles.otpRow}>
                {otpValues.map((val, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    onPaste={i === 0 ? handleOtpPaste : undefined}
                    style={{ ...styles.otpBox, ...(val ? styles.otpBoxFilled : {}) }}
                    onFocus={(e) => (e.target.style.borderColor = "#29553D")}
                    onBlur={(e) => (e.target.style.borderColor = val ? "#29553D" : "#e2ebe5")}
                    disabled={isVerifyingOtp}
                  />
                ))}
              </div>

              {otpError && <p style={styles.otpError}>{otpError}</p>}

              {/* Trust this device */}
              <label style={styles.trustLabel}>
                <input
                  type="checkbox"
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  style={{ marginRight: "0.5rem", accentColor: "#29553D" }}
                />
                Trust this device for 30 days
              </label>

              {/* Verify OTP button */}
              <button
                type="button"
                onClick={() => otpSubmitHandler()}
                style={isVerifyingOtp ? { ...styles.loginBtn, opacity: 0.8, cursor: "not-allowed" } : styles.loginBtn}
                disabled={isVerifyingOtp}
              >
                {isVerifyingOtp ? (
                  <>
                    <Spinner animation="border" size="sm" style={styles.btnSpinner} />
                    Verifying...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: "0.5rem" }} />
                    Verify OTP
                  </>
                )}
              </button>

              {/* Resend row */}
              <div style={styles.resendRow}>
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    style={isResending ? { ...styles.resendBtn, opacity: 0.6, cursor: "not-allowed" } : styles.resendBtn}
                    disabled={isResending}
                  >
                    {isResending ? (
                      <>
                        <Spinner animation="border" size="sm" style={{ width: "12px", height: "12px", marginRight: "0.35rem", borderWidth: "2px" }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faRedo} style={{ marginRight: "0.35rem" }} />
                        Resend OTP
                      </>
                    )}
                  </button>
                ) : (
                  <span style={styles.timerText}>
                    <FontAwesomeIcon icon={faClock} style={{ marginRight: "0.35rem" }} />
                    Resend in {resendTimer}s
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <p style={styles.footer}>Protected by Manshya Academy © 2026</p>
      </div>

      {/* Alerts */}
      {showLoginAlert &&
        (!validationError.errorMesage ? (
          isInactive ? (
            <ModalAlert size="lg" variant="danger"
              message="Your account is INACTIVE. Please contact admin to activate your account."
              resetCallback={resetCallback} />
          ) : loginAlertMsg ? (
            <ModalAlert size="lg" variant="warning"
              message={loginAlertMsg}
              resetCallback={resetCallback} />
          ) : (
            <ModalAlert size="lg" variant="danger"
              message="The credentials you entered are incorrect. Please check and try again."
              resetCallback={resetCallback} />
          )
        ) : (
          <ModalAlert size="lg" variant="danger"
            message={validationError.errorMesage}
            resetCallback={resetCallback} />
        ))}

      {showForgotPasswordAlert && (
        <ModalAlert size="lg" variant="info"
          message="Password reset is coming soon. Please contact admin to reset your password for now."
          resetCallback={setShowForgotPasswordAlert} />
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundSize: "cover",
    backgroundPosition: "left top",
    backgroundRepeat: "no-repeat",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "15%",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Segoe UI', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "2.25rem",
    width: "100%",
    maxWidth: "490px",
    boxShadow: "0 8px 40px rgba(30, 80, 50, 0.10)",
    position: "relative",
    zIndex: 1,
  },
  logoWrap: { textAlign: "center", marginBottom: "1.5rem" },
  logo: { maxWidth: "280px", marginBottom: "0.5rem" },
  stepWrapper: { transition: "opacity 0.3s ease, transform 0.3s ease" },
  heading: { fontSize: "1.6rem", fontWeight: 700, color: "#1a2e22", marginBottom: "0.3rem" },
  subheading: { fontSize: "0.875rem", color: "#8a9e92", marginBottom: "1.75rem" },
  fieldGroup: { marginBottom: "1.1rem" },
  label: {
    display: "block", fontSize: "0.7rem", fontWeight: 700,
    letterSpacing: "0.1em", color: "#8a9e92", marginBottom: "0.4rem",
  },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  inputIcon: { position: "absolute", left: "0.9rem", color: "#b0c4b8", fontSize: "0.95rem", pointerEvents: "none" },
  input: {
    width: "100%", height: "48px", border: "1.5px solid #e2ebe5",
    borderRadius: "10px", padding: "0 1rem 0 2.5rem", fontSize: "0.95rem",
    color: "#1a2e22", background: "#fff", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.2s",
  },
  eyeBtn: {
    position: "absolute", right: "0.9rem", background: "none",
    border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center",
  },
  forgotBtn: {
    background: "none", border: "none", color: "#2d6a4f",
    fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", padding: 0,
  },
  loginBtn: {
    width: "100%", height: "50px",
    background: "linear-gradient(135deg, #2d5a3d 0%, #1e4530 100%)",
    color: "#fff", border: "none", borderRadius: "10px",
    fontSize: "1rem", fontWeight: 600, cursor: "pointer",
    letterSpacing: "0.02em", transition: "opacity 0.2s",
    marginBottom: "1.25rem", display: "flex", alignItems: "center", justifyContent: "center",
  },
  btnSpinner: { marginRight: "0.5rem", borderWidth: "2px" },
  footer: { textAlign: "center", fontSize: "0.75rem", color: "#b0c4b8", margin: "1.25rem 0 0" },
  backBtn: {
    background: "none", border: "none", color: "#8a9e92", fontSize: "0.82rem",
    cursor: "pointer", padding: "0 0 1rem 0", display: "block", fontWeight: 500,
  },
  otpRow: { display: "flex", gap: "10px", justifyContent: "center", margin: "1.5rem 0 1rem" },
  otpBox: {
    width: "44px", height: "52px", border: "1.5px solid #e2ebe5",
    borderRadius: "10px", fontSize: "1.4rem", fontWeight: 700,
    textAlign: "center", color: "#1a2e22", background: "#f8fbf9",
    outline: "none", boxSizing: "border-box", transition: "border-color 0.18s, background 0.18s",
  },
  otpBoxFilled: { borderColor: "#29553D", background: "#eef5f1" },
  otpError: { color: "#c0392b", fontSize: "0.8rem", textAlign: "center", marginBottom: "0.75rem", marginTop: "-0.25rem" },
  trustLabel: {
    display: "flex", alignItems: "center", fontSize: "0.82rem",
    color: "#8a9e92", fontWeight: 500, cursor: "pointer",
    marginBottom: "1.25rem", userSelect: "none",
  },
  resendRow: { textAlign: "center", marginTop: "0.5rem" },
  resendBtn: {
    background: "none", border: "none", color: "#2d6a4f",
    fontSize: "0.875rem", fontWeight: 500, cursor: "pointer", padding: 0,
    display: "inline-flex", alignItems: "center",
  },
  timerText: { fontSize: "0.82rem", color: "#b0c4b8", fontWeight: 500 },
};

export default Login;
